import { useState, useEffect } from 'react';
import './App.css';
import Dashboard from './components/Dashboard';
import WardDetail from './components/WardDetail';
import Scenarios from './components/Scenarios';

export interface Council {
  id: string;
  name: string;
  ons_code: string;
  total_seats: number;
  green_seats_current: number;
  election_cycle: string;
  next_election: string;
}

export interface Ward {
  id: string;
  name: string;
  council_id: string;
  council_name: string;
  ons_code?: string;
  seats: number;
  green_incumbents?: number;
  green_vote_2024?: number;
  turnout_2024?: number;
  vote_target?: number;
  candidate_strength?: number;
  sweet_spot_score?: number;
  status?: 'priority' | 'hold' | 'low_roi' | 'defensive';
  green_agreement_current?: number;
  current_councillors?: string;
  councillor_count?: number;
}

export interface WardDetail extends Ward {
  lab_vote_2024: number;
  con_vote_2024: number;
  ld_vote_2024: number;
  other_vote_2024: number;
  age_median: number;
  students_percent: number;
  renters_percent: number;
  fragmentation_index: number;
  door_knocks_per_week: number;
  deliveries_per_week: number;
  phonebank_hours_per_week: number;
  canvass_support_score: number;
  drift_D: number;
  effective_dimension: number;
  green_vote_predicted_current: number;
  marginal_gain: number;
  saturation_K: number;
  saturation_max_share: number;
  resources_required_X: number;
  resources_delta_X: number;
  computed_at: string;
}

type View = 'dashboard' | 'ward' | 'scenarios';

function App() {
  const [view, setView] = useState<View>('dashboard');
  const [selectedCouncil, setSelectedCouncil] = useState<string>('bristol');
  const [selectedWard, setSelectedWard] = useState<string | null>(null);
  const [councils, setCouncils] = useState<Council[]>([]);
  const [loading, setLoading] = useState(true);

  const API_BASE = import.meta.env.VITE_API_BASE || 'https://green-targeting.sparsesupernova.workers.dev/api';

  useEffect(() => {
    fetchCouncils();
  }, []);

  const fetchCouncils = async () => {
    try {
      const res = await fetch(`${API_BASE}/councils`);
      const data = await res.json();
      setCouncils(data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch councils:', err);
      setLoading(false);
    }
  };

  const handleWardSelect = (wardId: string) => {
    setSelectedWard(wardId);
    setView('ward');
  };

  const handleBackToDashboard = () => {
    setView('dashboard');
    setSelectedWard(null);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading Green Targeting System...</p>
      </div>
    );
  }

  return (
    <div className="app">
      {/* Header */}
      <header className="app-header">
        <div className="logo">
          <div className="logo-dot"></div>
          <span>Sparse Supernova</span>
        </div>
        <h1>Green Party Ward Targeting</h1>
        <nav className="main-nav">
          <button
            className={view === 'dashboard' ? 'active' : ''}
            onClick={() => setView('dashboard')}
          >
            Dashboard
          </button>
          <button
            className={view === 'scenarios' ? 'active' : ''}
            onClick={() => setView('scenarios')}
          >
            Scenarios
          </button>
        </nav>
      </header>

      {/* Council Selector */}
      <div className="council-selector">
        <label>Council:</label>
        <select
          value={selectedCouncil}
          onChange={(e) => {
            setSelectedCouncil(e.target.value);
          }}
        >
          {councils.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {/* Main Content */}
      <main className="app-main">
        {view === 'dashboard' && (
          <Dashboard
            councilId={selectedCouncil}
            apiBase={API_BASE}
            onWardSelect={handleWardSelect}
          />
        )}

        {view === 'ward' && selectedWard && (
          <WardDetail
            wardId={selectedWard}
            apiBase={API_BASE}
            onBack={handleBackToDashboard}
          />
        )}

        {view === 'scenarios' && (
          <Scenarios councilId={selectedCouncil} apiBase={API_BASE} />
        )}
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <p>
          Powered by Universal Saturation Law &middot; Sparse Supernova Research &middot; 2026
        </p>
      </footer>
    </div>
  );
}

export default App;
